/**
 * Print-fail sound alert.
 *
 * Fires a distinct "buzz-buzz-buzz" tone (low sawtooth) when auto-print
 * fails on the admin POS device. Deliberately different from the bright
 * new-order chime in NotificationCenter so staff can tell them apart.
 *
 * Uses the Web Audio API — no external assets required. Handles the
 * browser autoplay policy by unlocking the AudioContext on the first
 * user interaction (which realistically has already happened by the time
 * an order arrives).
 *
 * Respects `localStorage['print_fail_alert_muted'] === 'true'` so staff
 * can silence it from a settings toggle in the future.
 */

let audioCtx = null;
let unlocked = false;
let unlockAttached = false;

const getCtx = () => {
  if (audioCtx) return audioCtx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  audioCtx = new AC();
  return audioCtx;
};

// Attach a one-time listener to unlock the AudioContext on first user
// interaction (required by Chrome/Safari autoplay policies).
const attachUnlockOnce = () => {
  if (unlockAttached) return;
  unlockAttached = true;
  const unlock = () => {
    const ctx = getCtx();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    unlocked = true;
    window.removeEventListener('click', unlock);
    window.removeEventListener('keydown', unlock);
    window.removeEventListener('touchstart', unlock);
  };
  window.addEventListener('click', unlock, { once: true, capture: true });
  window.addEventListener('keydown', unlock, { once: true, capture: true });
  window.addEventListener('touchstart', unlock, { once: true, capture: true });
};

attachUnlockOnce();

/**
 * Play the print-fail alert.
 *
 * Pattern: three short low-pitched sawtooth buzzes at 220Hz, 0.18s each,
 * spaced 0.06s apart — deliberately harsh to grab attention over a busy
 * kitchen but only ~0.75s total so it doesn't drag on.
 */
export const playPrintFailAlert = () => {
  try {
    if (localStorage.getItem('print_fail_alert_muted') === 'true') return;
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      // Best-effort resume; if the user hasn't interacted yet the browser
      // will silently reject — the amber highlight is still visible so we
      // don't need to fail loudly.
      ctx.resume().catch(() => {});
    }

    // Gentle limiter so layered oscillators don't clip.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 4;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.08;
    limiter.connect(ctx.destination);

    const now = ctx.currentTime;
    const buzz = (startOffset, duration = 0.18, freq = 220) => {
      const start = now + startOffset;
      const saw = ctx.createOscillator();
      saw.type = 'sawtooth';
      saw.frequency.value = freq;
      // Slight downward pitch bend for that "error" feel.
      saw.frequency.linearRampToValueAtTime(freq * 0.9, start + duration);
      const gain = ctx.createGain();
      saw.connect(gain);
      gain.connect(limiter);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.5, start + 0.01);
      gain.gain.setValueAtTime(0.5, start + duration - 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
      saw.start(start);
      saw.stop(start + duration + 0.02);
    };

    // Three-buzz alert
    buzz(0);
    buzz(0.24);
    buzz(0.48);
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('playPrintFailAlert failed:', err);
    return false;
  }
};

/**
 * Toggle mute state. Persisted in localStorage so it survives reloads.
 * Returns the new muted state.
 */
export const setPrintFailAlertMuted = (muted) => {
  try {
    localStorage.setItem('print_fail_alert_muted', muted ? 'true' : 'false');
  } catch (err) {
    // localStorage blocked (private mode / disabled) — safe to ignore.
    if (process.env.NODE_ENV !== 'production') console.debug('setPrintFailAlertMuted:', err);
  }
  return muted;
};

export const isPrintFailAlertMuted = () => {
  try {
    return localStorage.getItem('print_fail_alert_muted') === 'true';
  } catch {
    return false;
  }
};

export default {
  playPrintFailAlert,
  setPrintFailAlertMuted,
  isPrintFailAlertMuted,
};
